import { EmotionCacheProvider, ThemeProvider } from '@ori-ui/mui';
import { UniversalTokenProvider, doDevelopmentLogin } from '@ori/auth';
import { parseClientAppConfig } from '@ori/client-app-config';
import { ServiceUrlsProvider } from '@ori/configuration-manager';
import { ClientCultureProvider } from '@ori/culture';
import { EnvironmentProvider, getEnvironmentFromHost } from '@ori/environment';
import { FeatureTogglesProvider, getEnabledFeatures } from '@ori/feature-toggles';
import { Geolocation } from '@ori/geolocation-popup';
import { TranslationsProvider } from '@ori/i18n';
import { ApplicationInsights, ErrorBoundary, getClientApplicationInsightsConfig } from '@ori/logger';
import { LoginProvider } from '@ori/login';
import { ApiClientProvider as StaticApiClientProvider } from '@ori/static-api-client-react';
import { TopAreaFeature, defaultTranslationsWithNamespaces } from '@ori/top-area';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { EMOTION_CACHE_KEY } from './constants-generated';
import { includePersonalMenuScript } from './includePersonalMenuScript';
import { mainLogger } from './logger';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const localLogger = mainLogger.createChild('appRender');

/**
 * React app renderer that is used when app is used as a standalone app
 */
export const render = async rootElement => {
  const {
    config = ''
  } = rootElement.dataset;
  const {
    customerId,
    identityUrl,
    ...clientAppConfig
  } = parseClientAppConfig(config, ['currentLanguage', 'globalApiUrl', 'graphqlNetUrl', 'mobileOfficeApiUrl', 'staticApiUrl', 'tenant']);
  const {
    currentLanguage,
    globalApiUrl,
    mobileOfficeApiUrl,
    staticApiUrl,
    tenant,
    graphqlNetUrl,
    useInternationalShareAndEarn = false
  } = clientAppConfig;
  if (process.env.NODE_ENV === 'production') {
    ApplicationInsights.initialize(getClientApplicationInsightsConfig());
  } else {
    // when using Requestly we do not want to load token
    // eslint-disable-next-line no-lonely-if -- thats okay
    if (process.env.REACT_APP_USE_REQUESTLY === '1') {
      localLogger.debug('USING REQUESTLY');
    } else {
      if (!identityUrl) {
        throw new Error('Missing Identity URL!');
      }

      // Get token for local development
      await doDevelopmentLogin({
        identityUrl,
        tenant
      });
    }
  }
  const environment = getEnvironmentFromHost(window.location.host);
  const features = await getEnabledFeatures(Object.values(TopAreaFeature), {
    apiClientOptions: {
      baseUrl: staticApiUrl
    },
    tenant
  });
  createRoot(rootElement).render(/*#__PURE__*/_jsx(ErrorBoundary, {
    logger: localLogger,
    children: /*#__PURE__*/_jsx(ServiceUrlsProvider, {
      serviceUrls: {
        globalApiUrl,
        identityUrl,
        mobileOfficeApiUrl,
        staticApiUrl,
        graphQlNetUrl: graphqlNetUrl
      },
      children: /*#__PURE__*/_jsx(EnvironmentProvider, {
        environment: environment,
        tenant: tenant,
        children: /*#__PURE__*/_jsx(UniversalTokenProvider, {
          customerId: customerId,
          initialToken: "",
          children: /*#__PURE__*/_jsx(EmotionCacheProvider, {
            cacheKey: EMOTION_CACHE_KEY,
            children: /*#__PURE__*/_jsx(ThemeProvider, {
              children: /*#__PURE__*/_jsx(StaticApiClientProvider, {
                baseUrl: staticApiUrl,
                language: currentLanguage,
                tenant: tenant,
                children: /*#__PURE__*/_jsx(FeatureTogglesProvider, {
                  toggles: features,
                  children: /*#__PURE__*/_jsxs(ClientCultureProvider, {
                    tenant: tenant,
                    children: [/*#__PURE__*/_jsx(LoginProvider, {
                      children: /*#__PURE__*/_jsx(TranslationsProvider, {
                        language: currentLanguage,
                        defaultTranslationsWithNamespaces: defaultTranslationsWithNamespaces
                        // eslint-disable-next-line no-console -- we need to log errors
                        ,
                        clientFetchConfiguration: {
                          onError: console.error,
                          tenant,
                          baseUrl: staticApiUrl
                        },
                        children: /*#__PURE__*/_jsx(App, {
                          clientAppConfig: clientAppConfig
                        })
                      })
                    }), /*#__PURE__*/_jsx(Geolocation, {
                      enabled: useInternationalShareAndEarn
                    })]
                  })
                })
              })
            })
          })
        })
      })
    })
  }));

  // Include personal menu entrypoint
  includePersonalMenuScript();
};